/**
 * Project comments — flat thread, plain markdown body. Posts auto-
 * scroll to the bottom; deletes are soft (server-side) and the row
 * just disappears from the UI on success.
 *
 * Permission shape mirrors the back-end:
 *   • Everyone can read.
 *   • Commenter+ can post — UI hides the composer if canComment is
 *     false (server enforces too).
 *   • Authors and admins can delete — UI hides the X on rows the
 *     viewer can't act on.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { MessageSquare, Send, Trash2 } from 'lucide-react'
import { api, type ApiError, type CommentDto } from '../lib/api'
import { Markdown } from './Markdown'
import { Badge, StatusBox } from './ui/badge'
import { Button } from './ui/button'
import { Textarea } from './ui/input'
import { EmptyState } from './ui/empty-state'

type Props = {
  projectId: string
  accessToken: string
  /** Current viewer's user id — used to decide if Delete shows on a row. */
  currentUserId: string | undefined
  /** True when role >= commenter. Hides the composer when false. */
  canPost: boolean
  /** True when role >= admin. Lets the viewer delete other people's
   *  comments in addition to their own. */
  canDeleteAny: boolean
}

export function Comments({
  projectId,
  accessToken,
  currentUserId,
  canPost,
  canDeleteAny,
}: Props) {
  const [comments, setComments] = useState<CommentDto[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    api.comments
      .list(accessToken, projectId)
      .then((res) => {
        if (cancelled) return
        setComments(res.comments)
        setErr(null)
      })
      .catch((e: ApiError) => {
        if (!cancelled) setErr(e.title ?? 'Failed to load comments')
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, projectId, reloadKey])

  // Scroll to bottom on new content so the latest is in view.
  useEffect(() => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [comments])

  async function handlePost(e: FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body || posting) return
    setPosting(true)
    setErr(null)
    try {
      const created = await api.comments.create(accessToken, projectId, body)
      setComments((prev) => (prev ? [...prev, created] : [created]))
      setDraft('')
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Failed to post comment')
    } finally {
      setPosting(false)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this comment? It will be hidden from the thread.')) return
    setDeletingId(id)
    setErr(null)
    try {
      await api.comments.remove(accessToken, id)
      setComments((prev) => (prev ? prev.filter((c) => c.id !== id) : prev))
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Failed to delete comment')
      setReloadKey((k) => k + 1)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Comments</h3>
        <Badge variant="secondary" className="tabular-nums">
          {comments ? `${comments.length}` : '…'}
        </Badge>
      </header>

      {err && (
        <div className="px-4 pt-3">
          <StatusBox tone="destructive" className="text-xs">{err}</StatusBox>
        </div>
      )}

      <div ref={scrollerRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {comments === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : comments.length === 0 ? (
          <EmptyState
            icon={<MessageSquare />}
            title="No comments yet"
            body={
              canPost
                ? 'Start the conversation below — markdown works.'
                : 'Comments from your team will show up here.'
            }
          />
        ) : (
          comments.map((c) => {
            const canDeleteRow =
              canDeleteAny || c.author.id === currentUserId
            return (
              <article
                key={c.id}
                className="rounded-xl border bg-muted/30 p-3 text-sm"
              >
                <header className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
                    <span className="truncate font-medium text-foreground">
                      {c.author.name ?? c.author.email}
                    </span>
                    <time className="shrink-0 text-xs" dateTime={c.createdAt}>
                      {formatRelative(c.createdAt)}
                    </time>
                  </div>
                  {canDeleteRow && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={deletingId === c.id}
                      onClick={() => handleDelete(c.id)}
                      aria-label="Delete comment"
                      className="-mr-1 -mt-1 text-muted-foreground hover:text-destructive"
                      title={
                        c.author.id === currentUserId
                          ? 'Delete your comment'
                          : 'Delete (admin)'
                      }
                    >
                      <Trash2 />
                    </Button>
                  )}
                </header>
                <div className="text-foreground">
                  <Markdown text={c.body} />
                </div>
              </article>
            )
          })
        )}
      </div>

      {canPost && (
        <form onSubmit={handlePost} className="space-y-2 border-t p-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                handlePost(e)
              }
            }}
            placeholder="Leave a comment — markdown works (⌘+Enter to send)"
            disabled={posting}
            rows={3}
            maxLength={10_000}
            className="min-h-0 resize-none"
          />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={posting || !draft.trim()}>
              <Send /> {posting ? 'Posting…' : 'Post'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d ago`
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}
